import axios from 'axios'
import { api, type CheckRunDetailDto, fetchCheckRun } from './api'

export type PublishingOrganizationType =
  | 'InternalOwned'
  | 'AccreditedDistributor'
  | 'IndependentSupplier'

export interface WebsiteSourceDto {
  id: string
  targetId: string
  productId: string
  url: string
  label?: string
  publisherId?: string
  publisherName: string
  publisherType: PublishingOrganizationType
  market?: string
  locale?: string
  healthStatus?: string
  lastCheckedUtc?: string
}

export type ComplianceRunStage =
  | 'Queued'
  | 'Acquiring'
  | 'Extracting'
  | 'Comparing'
  | 'EvaluatingRules'
  | 'Completed'

export const RUN_PROGRESS_STEPS: readonly ComplianceRunStage[] = [
  'Queued',
  'Acquiring',
  'Extracting',
  'Comparing',
  'EvaluatingRules',
  'Completed',
] as const

export const ACTIVE_RUN_STATUSES = new Set([
  'Queued',
  'Acquiring',
  'Extracting',
  'Comparing',
  'EvaluatingRules',
  'InProgress',
  'Pending',
  'CancelRequested',
])

const TERMINAL_RUN_STATUSES = new Set([
  'Completed',
  'Failed',
  'Cancelled',
  'NeedsReview',
  'Incomplete',
])

export function resolveRunProgressStep(status: string, targetStages?: string[]): ComplianceRunStage {
  if (TERMINAL_RUN_STATUSES.has(status)) return 'Completed'
  if (status === 'Queued' || status === 'Pending') return 'Queued'

  const stepIndex = (step: string) => RUN_PROGRESS_STEPS.indexOf(step as ComplianceRunStage)

  if (targetStages && targetStages.length > 0) {
    const indices = targetStages.map(stepIndex).filter((index) => index >= 0)
    if (indices.length > 0) {
      if (indices.every((index) => index === RUN_PROGRESS_STEPS.length - 1)) return 'Completed'
      const activeIndices = indices.filter((index) => index < RUN_PROGRESS_STEPS.length - 1)
      if (activeIndices.length > 0) return RUN_PROGRESS_STEPS[Math.min(...activeIndices)]
      return RUN_PROGRESS_STEPS[Math.max(...indices)]
    }
  }

  const direct = stepIndex(status)
  if (direct >= 0) return RUN_PROGRESS_STEPS[direct]
  if (status === 'InProgress') return 'Acquiring'
  return 'Queued'
}

export function formatRunElapsed(startUtc?: string, endUtc?: string, nowMs = Date.now()): string {
  if (!startUtc) return ''
  const start = new Date(startUtc).getTime()
  const end = endUtc ? new Date(endUtc).getTime() : nowMs
  const seconds = Math.max(0, Math.floor((end - start) / 1000))
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

export function isActiveRunStatus(status: string) {
  return ACTIVE_RUN_STATUSES.has(status)
}

export type ComplianceRunOutcome =
  | 'Completed'
  | 'NeedsReview'
  | 'Incomplete'
  | 'Failed'
  | 'Cancelled'

export type ComparisonOutcome =
  | 'Match'
  | 'Mismatch'
  | 'MissingObserved'
  | 'MissingSoT'
  | 'NotApplicable'
  | 'NeedsReview'
  | 'TechnicalError'

export type ComparisonMode = 'Exact' | 'Pattern' | 'Semantic'

export interface FrozenRevisionDto {
  id?: string
  name?: string
  version?: string
  hash?: string
}

export interface ComplianceRunCountsDto {
  targets: number
  processedTargets: number
  findings: number
  matches: number
  mismatches: number
  needsReview: number
  technicalErrors: number
  missingObserved: number
}

export interface CoveredVariantDto {
  sourceOfTruthRecordId?: string
  productVariantId?: string
  variantLabel: string
  isAnchor: boolean
}

export interface ComplianceRunDetailDto {
  fieldCoverage?: { monitoredClaims: number; outOfScopeRegisterFields: number; prohibitedLanguageRules: number }
  id: string
  status: string
  outcome?: ComplianceRunOutcome
  isReplayable: boolean
  createdUtc: string
  startedUtc?: string
  completedUtc?: string
  reason?: string
  productId: string
  productCode?: string
  productName?: string
  productVariantId?: string
  productVariantName?: string
  sourceOfTruthRecordId: string
  sourceOfTruthVersion?: string
  sourceOfTruthHash?: string
  sourceOfTruth?: FrozenRevisionDto
  extractionProfile?: FrozenRevisionDto
  extractionProfileMatchedScope?: string
  coveredVariants?: CoveredVariantDto[]
  rulePack?: FrozenRevisionDto
  promptPack?: FrozenRevisionDto
  comparisonAlgorithmVersion?: string
  aiDeployment?: string
  aiModelVersion?: string
  initiatedBy?: string
  applicationVersion?: string
  counts: ComplianceRunCountsDto
}

export interface ComplianceTargetRunDto {
  id: string
  complianceRunId: string
  targetId: string
  url: string
  finalUrl?: string
  section?: string
  sectionLabel?: string
  publisherId?: string
  publisherName: string
  publisherType: PublishingOrganizationType
  market?: string
  locale?: string
  stage: ComplianceRunStage
  outcome?: ComplianceRunOutcome
  errorCode?: string
  errorMessage?: string
  captureCount: number
  extractionCount: number
  comparisonCount: number
  ruleResultCount: number
  findingCount: number
  evidenceCount: number
  evidenceComplete: boolean
  startedUtc?: string
  completedUtc?: string
}

export type ExtractionCandidateDisposition = 'Accepted' | 'Rejected' | 'Ambiguous' | 'Irrelevant'

export interface ComplianceExtractionDto {
  id: string
  targetRunId: string
  pageCaptureId: string
  canonicalField: string
  displayLabel?: string
  disposition: ExtractionCandidateDisposition
  mappingRationale?: string
  rawValue?: string
  normalizedValue?: string
  unit?: string
  evidenceQuote?: string
  evidenceLocation?: string
  sourceUrl: string
  confidence?: number
  extractionMethod: string
  model?: string
  promptVersion?: string
  createdUtc: string
  expectedValue?: string
  expectedUnit?: string
  expectedRequiredOnWebsite: boolean
}

export interface ComplianceReviewDecisionDto {
  id: string
  comparisonResultId: string
  decision: 'Match' | 'Mismatch' | 'NeedsReview'
  rationale: string
  decidedBy: string
  createdUtc: string
}

export interface ComplianceComparisonDto {
  id: string
  targetRunId: string
  pageCaptureId?: string
  sourceOfTruthFieldId?: string
  canonicalField: string
  displayLabel?: string
  variantLabel?: string
  productVariantId?: string
  expectedValue?: string
  expectedUnit?: string
  observedValue?: string
  observedUnit?: string
  mode: ComparisonMode
  outcome: ComparisonOutcome
  confidence?: number
  severity?: string
  clause?: string
  evidenceQuote?: string
  sourceUrl?: string
  requiredOnWebsite: boolean
  rationale?: string
  latestDecision?: ComplianceReviewDecisionDto
}

export interface ComplianceRuleResultDto {
  id: string
  targetRunId: string
  ruleId: string
  ruleKey: string
  ruleName: string
  ruleType: string
  outcome: 'Pass' | 'Fail' | 'Passed' | 'Failed' | 'NeedsReview' | 'TechnicalError' | 'NotApplicable'
  severity?: string
  clause?: string
  message?: string
  evidenceQuote?: string
  sourceUrl?: string
  pageCaptureId?: string
  findingId?: string
  expectedValue?: string
  expectedUnit?: string
  observedValue?: string
  observedUnit?: string
}

export interface EvidenceMetadataDto {
  id: string
  artifactId: string
  targetRunId: string
  pageCaptureId: string
  artifactType: string
  contentType?: string
  contentLength?: number
  sha256: string
  sourceUrl?: string
  createdUtc: string
  integrityStatus: 'Verified' | 'Missing' | 'HashMismatch' | string
  retentionUntilUtc?: string
}

export interface ComplianceRunPreflightDto {
  productId: string
  isReady: boolean
  approvedSourceOfTruthCount: number
  monitoredFieldCount: number
  requiredWebsiteFieldCount: number
  extractionProfileVersion?: string
  extractionProfileName?: string
  extractionProfileMatchedScope?: string
  aiConfigured: boolean
  blockingIssues: Array<{ code: string; message: string }>
  warnings: Array<{ code: string; message: string }>
}

export interface CreateComplianceRunRequest {
  productId: string
  sourceOfTruthRecordId: string
  rulePackId?: string | null
  targetIds: string[]
  reason?: string
  languageOverride?: string | null
}

export interface CreateComplianceRunResponse {
  id: string
  status: string
  targetCount: number
  message?: string
}

export interface CreateComplianceReviewDecisionRequest {
  decision: 'Match' | 'Mismatch' | 'NeedsReview'
  rationale: string
}

export type ComplianceRunResponse =
  | { kind: 'v2'; run: ComplianceRunDetailDto }
  | { kind: 'legacy'; run: CheckRunDetailDto }

export async function createComplianceRun(request: CreateComplianceRunRequest) {
  const { data } = await api.post<CreateComplianceRunResponse>('/v2/compliance-runs', request)
  return data
}

export async function fetchComplianceRun(id: string) {
  const { data } = await api.get<ComplianceRunDetailDto>(`/v2/compliance-runs/${id}`)
  return data
}

export async function fetchComplianceRunWithLegacyFallback(id: string): Promise<ComplianceRunResponse> {
  try {
    return { kind: 'v2', run: await fetchComplianceRun(id) }
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 404) throw error
    return { kind: 'legacy', run: await fetchCheckRun(id) }
  }
}

export async function fetchComplianceRunTargets(id: string) {
  const { data } = await api.get<ComplianceTargetRunDto[]>(`/v2/compliance-runs/${id}/targets`)
  return data
}

export async function fetchComplianceRunExtractions(id: string, targetRunId: string) {
  const { data } = await api.get<ComplianceExtractionDto[]>(
    `/v2/compliance-runs/${id}/targets/${targetRunId}/extractions`,
  )
  return data
}

export async function fetchComplianceRunComparisons(id: string, targetRunId: string) {
  const { data } = await api.get<ComplianceComparisonDto[]>(
    `/v2/compliance-runs/${id}/targets/${targetRunId}/comparisons`,
  )
  return data
}

export async function fetchComplianceRunRuleResults(id: string, targetRunId: string) {
  const { data } = await api.get<ComplianceRuleResultDto[]>(
    `/v2/compliance-runs/${id}/targets/${targetRunId}/rule-results`,
  )
  return data
}

export async function fetchComplianceRunEvidence(id: string) {
  const { data } = await api.get<EvidenceMetadataDto[]>(`/v2/compliance-runs/${id}/evidence`)
  return data
}

export async function downloadEvidenceContent(artifactId: string) {
  const response = await api.get<Blob>(`/v2/evidence/${artifactId}/content`, { responseType: 'blob' })
  return response.data
}

export async function createComplianceReviewDecision(
  comparisonResultId: string,
  request: CreateComplianceReviewDecisionRequest,
) {
  const { data } = await api.post<ComplianceReviewDecisionDto>(
    `/v2/comparison-results/${comparisonResultId}/decisions`,
    request,
  )
  return data
}

export async function fetchComplianceRunPreflight(productId: string) {
  try {
    const { data } = await api.get<ComplianceRunPreflightDto>('/v2/compliance-runs/preflight', {
      params: { productId },
    })
    return data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

export interface CreateSpotcheckBatchRequest {
  name?: string
  productIds: string[]
  distributorId?: string | null
  targetIds?: string[]
  sectionCodes?: string[]
  rulePackId?: string | null
  reason?: string
}

export interface CreateSpotcheckBatchResponse {
  batchId: string
  status: string
  productCount: number
  runCount: number
  skippedCount: number
  targetCount: number
  message: string
}

export interface SpotcheckBatchItemDto {
  id: string
  productId: string
  productNameSnapshot?: string
  productCodeSnapshot?: string
  checkRunId?: string
  status: string
  skipReason?: string
  targetCount: number
  runStatus?: string
  runOutcome?: string
}

export interface SpotcheckBatchDto {
  id: string
  name: string
  distributorId?: string
  distributorName?: string
  rulePackId?: string
  reason?: string
  status: string
  createdUtc: string
  createdBy: string
  productCount: number
  runCount: number
  skippedCount: number
  targetCount: number
  items: SpotcheckBatchItemDto[]
}

export async function createSpotcheckBatch(request: CreateSpotcheckBatchRequest) {
  const { data } = await api.post<CreateSpotcheckBatchResponse>('/v2/spotcheck-batches', request)
  return data
}

export async function fetchSpotcheckBatch(batchId: string) {
  const { data } = await api.get<SpotcheckBatchDto>(`/v2/spotcheck-batches/${batchId}`)
  return data
}

export async function downloadComplianceAuditorReport(checkRunId: string, format: 'md' | 'html' = 'md'): Promise<Blob> {
  const response = await api.get(`/v2/compliance-runs/${checkRunId}/auditor-report`, {
    params: { format },
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function downloadComplianceRunDocx(checkRunId: string): Promise<Blob> {
  const response = await api.get(`/v2/compliance-runs/${checkRunId}/report.docx`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function downloadSpotcheckAuditorReport(batchId: string, format: 'md' | 'html' = 'md'): Promise<Blob> {
  const response = await api.get(`/v2/spotcheck-batches/${batchId}/auditor-report`, {
    params: { format },
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function downloadSpotcheckDocxReport(batchId: string): Promise<Blob> {
  const response = await api.get(`/v2/spotcheck-batches/${batchId}/report.docx`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

export interface SpotcheckScheduleDto {
  id: string
  name: string
  ownerEmail: string
  frequency: string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  hourUtc: number
  minuteUtc: number
  cronExpression: string
  rulePackId?: string | null
  rulePackName?: string | null
  distributorId?: string | null
  productIds: string[]
  targetIds: string[]
  sectionCodes: string[]
  isActive: boolean
  lastRunUtc?: string | null
  lastBatchId?: string | null
  lastError?: string | null
  createdUtc: string
  createdBy: string
}

export interface CreateSpotcheckScheduleRequest {
  name: string
  ownerEmail: string
  frequency: 'Daily' | 'Weekly' | 'Monthly' | string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  hourUtc: number
  minuteUtc: number
  rulePackId?: string | null
  distributorId?: string | null
  productIds: string[]
  targetIds?: string[]
  sectionCodes?: string[]
}

export async function fetchSpotcheckSchedules() {
  const { data } = await api.get<SpotcheckScheduleDto[]>('/v2/spotcheck-schedules')
  return data
}

export async function createSpotcheckSchedule(request: CreateSpotcheckScheduleRequest) {
  const { data } = await api.post<SpotcheckScheduleDto>('/v2/spotcheck-schedules', request)
  return data
}

export async function pauseSpotcheckSchedule(id: string) {
  const { data } = await api.post<SpotcheckScheduleDto>(`/v2/spotcheck-schedules/${id}/pause`)
  return data
}

export async function resumeSpotcheckSchedule(id: string) {
  const { data } = await api.post<SpotcheckScheduleDto>(`/v2/spotcheck-schedules/${id}/resume`)
  return data
}

export async function deleteSpotcheckSchedule(id: string) {
  await api.delete(`/v2/spotcheck-schedules/${id}`)
}

